import { IconGlobe } from '@douyinfe/semi-icons';
import { Cascader } from '@douyinfe/semi-ui';
import { useMemo, useEffect } from 'react';
import useStore from '../../../../store';
import useUserStore from '@/store/user';
import { getCache } from '@/hook/useCache';
import { CacheEnum } from '@/enum/cache';

// 国家配置映射
const COUNTRY_CONFIG = {
  saudi: {
    flag: '🇸🇦',
    name: '沙特站',
    code: 'saudi',
  },
  uae: {
    flag: '🇦🇪',
    name: '阿联酋',
    code: 'uae',
  },
} as const;

const CountryMode = () => {
  const setCountryMode = useStore((state) => state.setCountryMode);
  const { subAccountList, fetchSubAccountList, userInfo } = useUserStore();

  // 组件挂载时获取子账号列表
  useEffect(() => {
    if (!subAccountList || subAccountList.length === 0) {
      console.log('CountryMode: 正在获取子账号列表...');
      fetchSubAccountList();
    }
  }, [subAccountList, fetchSubAccountList]);

  // 根据subAccountList构建级联数据
  const cascaderData = useMemo(() => {
    if (!subAccountList || subAccountList.length === 0) {
      return [];
    }

    return subAccountList.map((account) => {
      // 解析账号的国家列表
      const countries = account.country
        ? account.country
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
        : [];

      // 构建国家子选项
      const countryChildren = countries
        .filter(
          (countryCode) =>
            COUNTRY_CONFIG[countryCode as keyof typeof COUNTRY_CONFIG]
        )
        .map((countryCode) => {
          const config =
            COUNTRY_CONFIG[countryCode as keyof typeof COUNTRY_CONFIG];
          return {
            label: `${config.flag} ${config.name}`,
            value: `${account.id}_${countryCode}`,
            countryCode: countryCode,
            accountId: account.id,
            flag: config.flag,
          };
        });

      return {
        label: `${account.username} (${account.email})`,
        value: account.id,
        children: countryChildren,
        disabled: countryChildren.length === 0,
        username: account.username,
        email: account.email,
      };
    });
  }, [subAccountList]);

  // 获取默认值 - 根据userInfo和localStorage中的COUNTRY设置
  const defaultValue = useMemo(() => {
    if (cascaderData.length === 0) return undefined;

    // 从 localStorage 获取当前选中的国家
    const currentCountry = getCache({
      key: CacheEnum.userCountry,
      storage: localStorage,
    });

    // 如果有userInfo，根据userInfo中的id找到对应账号
    if (userInfo?.id) {
      const currentAccount = cascaderData.find(
        (account) => account.value === userInfo.id
      );
      if (
        currentAccount &&
        currentAccount.children &&
        currentAccount.children.length > 0
      ) {
        // 如果localStorage中有country信息，尝试匹配对应国家
        if (currentCountry) {
          const matchingCountry = currentAccount.children.find(
            (child) => child.countryCode === currentCountry
          );
          if (matchingCountry) {
            return [currentAccount.value, matchingCountry.value];
          }
        }
        // 否则返回第一个国家
        return [currentAccount.value, currentAccount.children[0].value];
      }
    }

    // 如果没有userInfo或找不到匹配账号，返回第一个账号的第一个国家
    const firstAccount = cascaderData[0];
    if (firstAccount.children && firstAccount.children.length > 0) {
      return [firstAccount.value, firstAccount.children[0].value];
    }

    return undefined;
  }, [cascaderData, userInfo]);

  // 处理选择变化
  const handleChange = async (value: any) => {
    if (Array.isArray(value) && value.length === 2) {
      // value[1] 格式为 "accountId_countryCode"
      const selectedItem = value[1] as string;
      const [accountId, countryCode] = selectedItem.split('_');
      if (countryCode && accountId) {
        // 设置国家模式
        setCountryMode(countryCode);

        // 重新获取选中账号在该国家下的用户详细信息
        const { fetchUserDetails } = useUserStore.getState();
        try {
          await fetchUserDetails(accountId, countryCode);
          window.location.reload();
        } catch (error) {
          console.error('切换国家时获取用户信息失败:', error);
        }
      }
    }
  };

  // 自定义显示格式
  const displayRender = (selected: any) => {
    // 处理选中的路径数组
    if (Array.isArray(selected) && selected.length === 2) {
      const accountLabel = selected[0]; // 第一个值是账号的label
      const countryLabel = selected[1]; // 第二个值是国家的label
      // 根据label反推countryCode（label格式：🇸🇦 沙特站）
      if (typeof countryLabel === 'string') {
        const countryEntry = Object.entries(COUNTRY_CONFIG).find(([, config]) =>
          countryLabel.includes(config.name)
        );

        if (countryEntry) {
          const [countryCode, config] = countryEntry;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px', marginLeft: '4px' }}>
                {accountLabel}&ensp;/
              </span>
              <span style={{ fontSize: '16px' }}>{config.flag}</span>
              <span>{config.name}</span>
            </div>
          );
        }
      }
    }

    return '请选择账号和国家';
  };

  // 如果没有数据，显示默认选项
  if (cascaderData.length === 0) {
    return (
      <div
        style={{
          width: 180,
          marginRight: 10,
          padding: '6px 12px',
          backgroundColor: 'white',
          border: '1px solid #e8e8e8',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#999',
        }}
      >
        <IconGlobe />
        <span>暂无账号数据</span>
      </div>
    );
  }

  return (
    <Cascader
      style={{ width: 'auto', marginRight: 10, minWidth: '280px' }}
      treeData={cascaderData}
      placeholder='请选择账号和国家'
      defaultValue={defaultValue}
      onChange={handleChange}
      displayRender={displayRender}
      prefix={<IconGlobe />}
      showClear={false}
      autoAdjustOverflow={true}
      dropdownStyle={{ minWidth: '280px' }}
    />
  );
};

export default CountryMode;
